// src/services/timekeepingService.js - Sistema Unificato per tutti gli Scanner
import { db, auth } from '../firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  runTransaction,
  addDoc, 
  query, 
  where, 
  serverTimestamp, 
  Timestamp, 
  limit,
  orderBy
} from 'firebase/firestore';
import { getKioskDeviceLocation, getLocationName } from '../utils/locationUtils';

import rapportiniAutoService from './rapportiniAutoService';

/**
 * =====================================================
 * 🎯 SISTEMA UNIFICATO TIMEKEEPING
 * =====================================================
 * 
 * OBIETTIVI:
 * 1. Tutti gli scanner (kiosk, admin, responsabile) usano la stessa logica
 * 2. Possibilità di aprire turno con uno scanner e chiudere con un altro
 * 3. Calcolo ore consistente senza duplicazioni o conflitti
 * 4. Mantenimento delle regole esistenti (autisti, auto-chiusura, ecc.)
 * 5. Tracciabilità della fonte di ogni timbratura
 * 
 * STRATEGIA:
 * - UNA SOLA sessione attiva per utente (indipendentemente dallo scanner)
 * - Calcolo ore basato SOLO sui timestamp di ingresso/uscita
 * - Sincronizzazione workHours senza duplicazioni
 * - Logging dettagliato per audit trail
 */

/**
 * Funzione per identificare autisti
 */
const isAutista = (userData) => {
  return userData?.incarico === 'Autista';
};

/**
 * Normalizza le informazioni dello scanner per tracking unificato
 */
const normalizeScanner = (scanInfo = {}) => {
  const scannerType = scanInfo.deviceType || 'unknown';
  const isKiosk = scanInfo.kioskMode === true;
  const isResponsabile = scanInfo.responsableAction === true;
  const isAdmin = scanInfo.isAdmin === true;
  
  let normalizedType;
  if (isKiosk) {
    normalizedType = 'kiosk';
  } else if (isResponsabile) {
    normalizedType = 'responsabile';
  } else if (isAdmin) {
    normalizedType = 'admin';
  } else {
    normalizedType = 'generic';
  }
  
  return {
    scannerType: normalizedType,
    deviceId: scanInfo.deviceId || 'unknown',
    deviceType: scannerType,
    isKiosk,
    isResponsabile,
    isAdmin,
    timestamp: new Date().toISOString(),
    originalScanInfo: scanInfo
  };
};

/**
 * 🔒 Verifica che il dispositivo scanner sia attivo (non messo in pausa dall'admin)
 *
 * Il pulsante "pausa" (⏸️) nel tab Dispositivi dell'admin imposta
 * `scanDevices/{deviceId}.active = false`. Questa funzione fa rispettare quel
 * flag lato timbratura: se il documento del dispositivo esiste ed è disattivato,
 * la timbratura viene rifiutata.
 *
 * Comportamento prudente: blocca SOLO quando il documento esiste e `active === false`.
 * Se il deviceId è assente/vuoto o il documento non esiste (es. scanner admin
 * generico non registrato), non blocca nulla.
 *
 * @param {string} deviceId - ID del dispositivo (= ID documento in scanDevices)
 * @throws {Error} se il dispositivo è stato messo in pausa dall'amministratore
 */
const assertDeviceActive = async (deviceId) => {
  if (!deviceId || typeof deviceId !== 'string' || deviceId.trim() === '') {
    return; // Nessun deviceId (es. scanner admin) → nessun controllo
  }

  try {
    const deviceRef = doc(db, 'scanDevices', deviceId);
    const deviceSnap = await getDoc(deviceRef);

    if (!deviceSnap.exists()) {
      // Dispositivo non registrato: non blocchiamo (comportamento invariato)
      return;
    }

    const deviceData = deviceSnap.data();
    if (deviceData.active === false) {
      console.warn(`🚫 [DeviceCheck] Dispositivo "${deviceId}" in pausa - timbratura rifiutata`);
      throw new Error('Questo dispositivo è stato messo in pausa dall\'amministratore. Timbratura non consentita.');
    }
  } catch (error) {
    // Rilancia solo l'errore di dispositivo in pausa; per errori di lettura
    // (es. rete/permessi) non blocchiamo la timbratura.
    if (error?.message?.includes('messo in pausa')) {
      throw error;
    }
    console.error(`⚠️ [DeviceCheck] Errore verifica stato dispositivo "${deviceId}" (non bloccante):`, error);
  }
};

/**
 * Oltre questa durata un turno ancora aperto è una "uscita dimenticata" e viene
 * auto-chiuso a 8 ore standard. Fino a questa soglia un'uscita timbrata è
 * un'uscita reale: le ore si calcolano con le regole uniformi, straordinario
 * incluso. I turni reali oltre le 13 ore vengono segnalati al pannello HR dalla
 * Cloud Function `onLongShiftCompleted` (functions/longShiftNotification.js).
 *
 * ⚠️ Tenere allineato con FORGOTTEN_CLOCKOUT_HOURS in functions/index.js
 */
const FORGOTTEN_CLOCKOUT_HOURS = 18;

/**
 * Giorni di calendario tra la data di ingresso e quella di uscita (YYYY-MM-DD)
 */
const getDaysBetween = (startDate, endDate) => {
  const days = Math.round(
    (Date.parse(`${endDate}T00:00:00Z`) - Date.parse(`${startDate}T00:00:00Z`)) / 86400000
  );
  return Number.isFinite(days) ? days : (startDate !== endDate ? 1 : 0);
};

/**
 * Calcola l'orario di auto-chiusura (ingresso + 8 ore)
 */
const calculateAutoCloseTime = (clockInTime, clockInDate) => {
  const [inHours, inMinutes] = clockInTime.split(':').map(Number);
  const inTotalMinutes = inHours * 60 + inMinutes;
  
  // Aggiungi 8 ore (480 minuti)
  const autoCloseMinutes = inTotalMinutes + 480;
  
  let autoCloseDate = clockInDate;
  let finalMinutes = autoCloseMinutes;
  
  // Se supera la mezzanotte (1440 minuti), passa al giorno successivo
  if (autoCloseMinutes >= 1440) {
    const nextDate = new Date(clockInDate);
    nextDate.setDate(nextDate.getDate() + 1);
    
    const year = nextDate.getFullYear();
    const month = String(nextDate.getMonth() + 1).padStart(2, '0');
    const day = String(nextDate.getDate()).padStart(2, '0');
    autoCloseDate = `${year}-${month}-${day}`;
    
    finalMinutes = autoCloseMinutes - 1440;
  }
  
  const autoCloseHours = Math.floor(finalMinutes / 60);
  const autoCloseMin = finalMinutes % 60;
  const autoCloseTime = `${String(autoCloseHours).padStart(2, '0')}:${String(autoCloseMin).padStart(2, '0')}`;
  
  return {
    clockOutTime: autoCloseTime,
    clockOutDate: autoCloseDate,
    totalHours: 8,
    standardHours: 8,
    overtimeHours: 0,
    autoClosedReason: `Auto-chiuso: superato limite orario. Assegnate 8 ore standard (${clockInTime} + 8h = ${autoCloseTime})`
  };
};

/**
 * Validazione unificata dell'orario di uscita
 * UNICA REGOLA: oltre FORGOTTEN_CLOCKOUT_HOURS l'uscita è considerata dimenticata
 * e il turno viene auto-chiuso; sotto quella soglia l'uscita è sempre accettata.
 */
const validateClockOutTime = (clockInTime, clockInDate, clockOutTime, clockOutDate) => {
  // Calcola la durata del turno in ore
  const [inHours, inMinutes] = clockInTime.split(':').map(Number);
  const [outHours, outMinutes] = clockOutTime.split(':').map(Number);

  const inTotalMinutes = inHours * 60 + inMinutes;
  let outTotalMinutes = outHours * 60 + outMinutes;

  // Se l'uscita è in un giorno successivo, aggiungi 24 ore per ogni giorno
  outTotalMinutes += getDaysBetween(clockInDate, clockOutDate) * 1440;

  const durationMinutes = outTotalMinutes - inTotalMinutes;
  const durationHours = durationMinutes / 60;
  
  console.log(`🕐 Validazione turno: ${clockInTime} (${clockInDate}) → ${clockOutTime} (${clockOutDate})`);
  console.log(`⏱️ Durata turno: ${durationHours.toFixed(2)} ore`);
  
  // REGOLA 1: Turno troppo corto (< 1 minuto)
  if (durationMinutes < 1) {
    return {
      isValid: false,
      reason: `Durata turno troppo breve: ${durationMinutes} minuti. Minimo 1 minuto.`
    };
  }
  
  // REGOLA 2: Uscita dimenticata (> 18 ore dall'ingresso) → Auto-chiusura
  if (durationHours > FORGOTTEN_CLOCKOUT_HOURS) {
    const autoCloseData = calculateAutoCloseTime(clockInTime, clockInDate);

    return {
      isValid: false,
      autoClose: true,
      reason: `Turno troppo lungo (${durationHours.toFixed(1)} ore). Auto-chiuso a 8 ore standard.`,
      autoCloseData: {
        ...autoCloseData,
        autoClosedReason: `Auto-chiuso: uscita non timbrata entro ${FORGOTTEN_CLOCKOUT_HOURS} ore (${durationHours.toFixed(1)}h). Assegnate 8 ore standard.`
      }
    };
  }

  // REGOLA 3: Turno valido (≤ 18 ore) - sempre accettato, ore reali
  console.log(`✅ Turno valido: ${durationHours.toFixed(2)} ore - ACCETTATO`);
  
  return {
    isValid: true,
    reason: null
  };
};

/**
 * 🎯 CALCOLO ORE UNIFICATO - Regole uniformi per tutti
 * REGOLE UGUALI PER TUTTI (autisti e standard):
 * - < 1 ora effettiva → 1 ora registrata (minimo)
 * - 1-6 ore effettive → Ore effettive registrate
 * - 7-10 ore effettive → 8 ore standard registrate (arrotondate automaticamente)
 * - 11-18 ore effettive → 8 ore standard + straordinario oltre le 10 ore
 *   (11h → +1, 12h → +2, 13h → +3, ...)
 * - oltre 18 ore → uscita dimenticata: auto-chiusura a 8 ore standard
 *   (gestito da validateClockOutTime)
 *
 * NOTA: Nessuna detrazione pausa pranzo (la fascia 7-10h→8h gestisce già le pause naturali)
 */
const calculateWorkingHoursUnified = (clockInTime, clockInDate, clockOutTime, clockOutDate, userData) => {
  const [inHours, inMinutes] = clockInTime.split(':').map(Number);
  const [outHours, outMinutes] = clockOutTime.split(':').map(Number);
  
  let inTotalMinutes = inHours * 60 + inMinutes;
  let outTotalMinutes = outHours * 60 + outMinutes;
  
  // Se uscita è il giorno dopo, aggiungi 24 ore all'uscita
  if (clockOutDate !== clockInDate) {
    outTotalMinutes += 1440;
  }
  
  let totalWorkedMinutes = outTotalMinutes - inTotalMinutes;
  
  if (totalWorkedMinutes <= 0) {
    totalWorkedMinutes += 1440;
  }
  
  const totalHours = totalWorkedMinutes / 60;
  const isDriverCategory = isAutista(userData);
  const isMultiDay = clockInDate !== clockOutDate;
  
  console.log(`💼 CALCOLO CON REGOLE UNIFORMI: ${totalHours.toFixed(2)}h`);
  console.log(`📅 Turno: ${isMultiDay ? 'MULTI-GIORNO' : 'STESSO GIORNO'}`);
  
  let netWorkedMinutes = totalWorkedMinutes;
  
  // NESSUNA DETRAZIONE PAUSA PRANZO
  // La fascia 7-10h→8h gestisce già le pause naturali
  console.log('⚙️ Applicazione regole uniformi per tutti (nessuna detrazione pausa)');
  netWorkedMinutes = totalWorkedMinutes;
  
  // Distribuzione delle ore per data
  const workDistribution = [];
  
  if (clockInDate === clockOutDate) {
    // STESSO GIORNO - Calcolo semplificato con regole uniformi
    let roundedHours = Math.floor(netWorkedMinutes / 60);
    const remainingMinutes = netWorkedMinutes % 60;
    
    if (remainingMinutes >= 30) {
      roundedHours += 1;
    }
    
    // MINIMO 1 ORA: Turni brevissimi (< 1h) registrano almeno 1 ora
    if (roundedHours < 1 && netWorkedMinutes > 0) {
      roundedHours = 1;
      console.log(`⚠️ Turno brevissimo (${netWorkedMinutes} min) → Registrato minimo 1h`);
    }
    
    let standardHours, overtimeHours;
    
    // REGOLE UNIFORMI PER TUTTI
    if (roundedHours <= 6) {
      // 6 ore o meno → registra le ore effettive
      standardHours = roundedHours;
      overtimeHours = 0;
    } else if (roundedHours <= 10) {
      // Da 7 a 10 ore → registra sempre 8 ore standard
      standardHours = 8;
      overtimeHours = 0;
    } else {
      // Oltre 10 ore → 8 ore standard + straordinario
      standardHours = 8;
      overtimeHours = roundedHours - 10; // Straordinario oltre le 10h
    }
    
    console.log(`✅ REGOLE UNIFORMI - Ore effettive: ${roundedHours}h | Registrate: ${standardHours}h standard + ${overtimeHours}h straordinario`);
    
    workDistribution.push({
      date: clockInDate,
      totalMinutes: netWorkedMinutes,
      totalHours: roundedHours,
      standardHours,
      overtimeHours,
      notes: `Turno ${clockInTime}-${clockOutTime} (Regole uniformi)`
    });
  } else {
    // MULTI-GIORNO
    let totalRoundedHours = Math.floor(netWorkedMinutes / 60);
    const remainingMinutes_total = netWorkedMinutes % 60;
    
    if (remainingMinutes_total >= 30) {
      totalRoundedHours += 1;
    }
    
    if (totalRoundedHours < 1 && netWorkedMinutes > 0) {
      totalRoundedHours = 1;
    }
    
    // Applica regole sul TOTALE del turno
    let totalStandardHours, totalOvertimeHours;
    
    if (totalRoundedHours <= 6) {
      totalStandardHours = totalRoundedHours;
      totalOvertimeHours = 0;
    } else if (totalRoundedHours <= 10) {
      totalStandardHours = 8;
      totalOvertimeHours = 0;
    } else {
      totalStandardHours = 8;
      totalOvertimeHours = totalRoundedHours - 10;
    }
    
    console.log(`🎯 Turno multigiorno TOTALE: ${totalRoundedHours}h → ${totalStandardHours}h std + ${totalOvertimeHours}h straord`);
    
    // ✅ Tutto attribuito alla data di INIZIO — nessun while, nessuna distribuzione proporzionale
    workDistribution.push({
      date: clockInDate,
      totalMinutes: netWorkedMinutes,
      totalHours: totalRoundedHours,
      standardHours: totalStandardHours,
      overtimeHours: totalOvertimeHours,
      notes: `Turno notturno ${clockInTime}-${clockOutTime} (${clockInDate}→${clockOutDate})`
    });
    
    console.log(`✅ Turno notturno: tutto attribuito a ${clockInDate}: ${totalRoundedHours}h → ${totalStandardHours}h std + ${totalOvertimeHours}h straord`);
  }
  
  // Calcola totali
  const totalRoundedHours = workDistribution.reduce((sum, day) => sum + day.totalHours, 0);
  const totalStandardHours = workDistribution.reduce((sum, day) => sum + day.standardHours, 0);
  const totalOvertimeHours = workDistribution.reduce((sum, day) => sum + day.overtimeHours, 0);
  
  return {
    totalMinutesWorked: totalWorkedMinutes,
    netMinutesWorked: netWorkedMinutes,
    totalHours: totalRoundedHours,
    standardHours: totalStandardHours,
    overtimeHours: totalOvertimeHours,
    workDistribution,
    isMultiDay: clockInDate !== clockOutDate,
    userCategory: 'Regole uniformi per tutti',
    categoryRules: '<1h→1h min, 1-6h→ore effettive, 7-10h→8h std, 11-18h→8h + straord oltre le 10h, >18h→auto-close 8h',
    message: `Calcolo con regole uniformi: ${totalRoundedHours}h totali (${totalStandardHours}h std + ${totalOvertimeHours}h straord.)`
  };
};

/**
 * Regole uniformi applicate ai minuti lavorati in una giornata
 * (stesse soglie di calculateWorkingHoursUnified).
 * ⚠️ Tenere allineato con functions/workHoursDay.js
 */
const applyUniformRules = (workedMinutes) => {
  let roundedHours = Math.floor(workedMinutes / 60);
  if (workedMinutes % 60 >= 30) roundedHours += 1;
  if (roundedHours < 1 && workedMinutes > 0) roundedHours = 1;

  if (roundedHours <= 6) return { totalHours: roundedHours, standardHours: roundedHours, overtimeHours: 0 };
  if (roundedHours <= 10) return { totalHours: roundedHours, standardHours: 8, overtimeHours: 0 };
  return { totalHours: roundedHours, standardHours: 8, overtimeHours: roundedHours - 10 };
};

// Un turno chiuso per uscita dimenticata (dall'app o dalla funzione schedulata)
// vale 8 ore fisse
const isForgottenClockOut = (shift) => shift.status === 'auto-closed' || Boolean(shift.autoCloseInfo);

/**
 * Minuti realmente lavorati in un turno chiuso
 */
const getShiftWorkedMinutes = (shift) => {
  if (isForgottenClockOut(shift)) return 480;

  if (shift.clockInTime && shift.clockOutTime) {
    const [inHours, inMinutes] = shift.clockInTime.split(':').map(Number);
    const [outHours, outMinutes] = shift.clockOutTime.split(':').map(Number);
    const minutes = (outHours * 60 + outMinutes) - (inHours * 60 + inMinutes) +
      getDaysBetween(shift.date, shift.clockOutDate || shift.date) * 1440;
    if (Number.isFinite(minutes) && minutes > 0) return minutes;
  }

  return (parseFloat(shift.totalHours) || 0) * 60;
};

/**
 * 🔄 SINCRONIZZAZIONE WORKHOURS UNIFICATA
 *
 * Ricalcola il cartellino di ogni giornata dalle timbrature reali: somma i
 * minuti effettivi di tutti i turni chiusi di quella data e applica le regole
 * uniformi una sola volta sul totale.
 *
 * Non somma più le ore già scritte nel cartellino: i valori lì presenti sono
 * ore già convertite (8 + straordinario) e riapplicarvi le regole faceva
 * perdere 2 ore di straordinario ogni volta che il giorno esisteva già, anche
 * vuoto (es. mese precompilato da un pannello).
 *
 * La scrittura avviene in transazione e ogni variazione viene accodata allo
 * storico del giorno (`entry.history`). La stessa logica gira lato server in
 * functions/workHoursDay.js alla chiusura di ogni timbratura.
 */
const syncToWorkHoursUnified = async (userId, workDistribution, source = 'timbratura') => {
  const syncResults = [];
  const dates = [...new Set((workDistribution || []).map(dayWork => dayWork.date).filter(Boolean))];

  for (const date of dates) {
    try {
      const [year, month, day] = date.split('-');

      if (!year || !month || !day) {
        console.error("❌ Formato data non valido:", date);
        syncResults.push({ date, success: false, error: "Invalid date format" });
        continue;
      }

      // Tutti i turni chiusi della giornata (stessa query di clockIn)
      const shiftsSnapshot = await getDocs(query(
        collection(db, "timekeeping"),
        where("userId", "==", userId),
        where("date", "==", date),
        where("status", "in", ["completed", "auto-closed"])
      ));
      const shifts = shiftsSnapshot.docs
        .map(shiftDoc => shiftDoc.data())
        .sort((a, b) => String(a.clockInTime).localeCompare(String(b.clockInTime)));

      if (shifts.length === 0) {
        syncResults.push({ date, success: false, error: "No closed shifts" });
        continue;
      }

      const workedMinutes = shifts.reduce((sum, shift) => sum + getShiftWorkedMinutes(shift), 0);
      const dayHours = applyUniformRules(workedMinutes);
      const shiftsLabel = shifts
        .map(shift => `${shift.clockInTime}-${shift.clockOutTime}` +
          (isForgottenClockOut(shift) ? ' (uscita non timbrata, 8 ore assegnate)' : ''))
        .join(' + ');
      const baseNotes = `${shifts.length > 1 ? 'Turni' : 'Turno'} ${shiftsLabel} (Regole uniformi)`;

      const normalizedMonth = month.replace(/^0+/, '');
      const workHoursRef = doc(db, "workHours", `${userId}_${normalizedMonth}_${year}`);
      const nowIso = new Date().toISOString();

      const result = await runTransaction(db, async (transaction) => {
        const workHoursSnap = await transaction.get(workHoursRef);
        const entries = workHoursSnap.exists() ? [...(workHoursSnap.data().entries || [])] : [];
        const entryIndex = entries.findIndex(entry => entry.date === date);
        const current = entryIndex >= 0 ? entries[entryIndex] : null;

        // 🛑 Non sovrascrivere entries con lettere speciali (M, P, F, A, C)
        if (current && typeof current.total === 'string' && ["M", "P", "A", "F", "C"].includes(current.total)) {
          console.log(`⚠️ Entry speciale esistente per ${date} (${current.total}) - SKIP`);
          return { success: false, error: "Manual entry exists" };
        }

        // 🛑 Non sovrascrivere entries inserite manualmente dall'admin
        if (current?.notes?.includes("Manual entry") || current?.notes?.includes("Inserted by admin")) {
          console.log(`⚠️ Entry amministrativa esistente per ${date} - SKIP`);
          return { success: false, error: "Admin entry exists" };
        }

        const currentTotal = parseFloat(current?.total) || 0;
        const currentOvertime = parseFloat(current?.overtime) || 0;
        const valuesChanged = !current ||
          currentTotal !== dayHours.standardHours || currentOvertime !== dayHours.overtimeHours;

        // Se il ricalcolo sostituisce ore inserite a mano, resta scritto nella nota
        let notes = baseNotes;
        if (current?.manualEdit) {
          notes = valuesChanged
            ? `${baseNotes} [Ricalcolato dalle timbrature: sostituisce ${currentTotal}+${currentOvertime} inserito a mano da ${current.modifierName || 'utente'}]`
            : (current.notes || baseNotes);
        }

        const entry = {
          ...(current || {}),
          date,
          total: dayHours.standardHours,
          overtime: dayHours.overtimeHours,
          notes,
          lastScanner: 'unified-system',
          lastUpdated: nowIso
        };

        if (valuesChanged) {
          entry.manualEdit = false;
          entry.history = [
            ...(current?.history || []),
            {
              at: nowIso,
              source,
              byId: null,
              byName: 'Sistema (timbrature)',
              before: current ? { total: current.total ?? 0, overtime: current.overtime ?? 0, notes: current.notes || '' } : null,
              after: { total: dayHours.standardHours, overtime: dayHours.overtimeHours, notes }
            }
          ];
        }

        if (entryIndex >= 0) entries[entryIndex] = entry;
        else entries.push(entry);

        if (workHoursSnap.exists()) {
          transaction.update(workHoursRef, { entries, lastUpdated: serverTimestamp() });
        } else {
          transaction.set(workHoursRef, {
            userId,
            month: normalizedMonth,
            year,
            entries,
            createdAt: serverTimestamp(),
            lastUpdated: serverTimestamp()
          });
        }

        return {
          success: true,
          action: current ? "recalculated" : "created_new",
          details: `${shifts.length} turni, ${workedMinutes} min → ${dayHours.standardHours}h std + ${dayHours.overtimeHours}h straord`
        };
      });

      console.log(`🔄 UNIFICATO ${date}: ${result.details || result.error}`);
      syncResults.push({ date, ...result });

    } catch (error) {
      console.error(`❌ Errore sync unificato per ${date}:`, error);
      syncResults.push({ date, success: false, error: error.message });
    }
  }

  return syncResults;
};

/**
 * Auto-chiude le sessioni scadute - VERSIONE UNIFICATA
 */
const autoCloseOpenSessions = async (userId) => {
  try {
    console.log(`🔍 [AutoClose] Controllo sessioni scadute per user: ${userId}`);
    
    const now = new Date();
    const timekeepingRef = collection(db, "timekeeping");
    
    // Trova tutte le sessioni in corso per l'utente
    const activeQuery = query(
      timekeepingRef,
      where("userId", "==", userId),
      where("status", "==", "in-progress")
    );
    
    const activeSnapshot = await getDocs(activeQuery);
    console.log(`📊 [AutoClose] Trovate ${activeSnapshot.size} sessioni attive`);
    
    if (activeSnapshot.empty) {
      console.log(`✅ [AutoClose] Nessuna sessione attiva da controllare`);
      return { processed: 0, closed: 0 };
    }
    
    let processedCount = 0;
    let closedCount = 0;
    
    for (const docSnap of activeSnapshot.docs) {
      const record = docSnap.data();
      processedCount++;
      
      console.log(`🔍 [AutoClose] Controllo sessione ${docSnap.id}: ${record.date} ${record.clockInTime}`);
      
      // Calcola durata del turno
      const clockInDateTime = new Date(`${record.date}T${record.clockInTime}:00`);
      const durationHours = (now - clockInDateTime) / (1000 * 60 * 60);
      
      console.log(`⏱️ [AutoClose] Durata turno: ${durationHours.toFixed(2)} ore`);
      
      // LOGICA UNIFICATA: chiudi solo le uscite dimenticate (oltre 18 ore)
      if (durationHours > FORGOTTEN_CLOCKOUT_HOURS) {
        console.log(`🚨 [AutoClose] USCITA DIMENTICATA (${durationHours.toFixed(1)}h > ${FORGOTTEN_CLOCKOUT_HOURS}h) - Auto-chiudo`);
        
        // Calcola orario auto-chiusura
        const autoCloseData = calculateAutoCloseTime(record.clockInTime, record.date);
        
        // Usa calcolo ore unificato per auto-chiusura
        const userData = { incarico: record.userCategory === 'Autista' ? 'Autista' : 'Standard' };
        const workResult = calculateWorkingHoursUnified(
          record.clockInTime,
          record.date,
          autoCloseData.clockOutTime,
          autoCloseData.clockOutDate,
          userData
        );
        
        // Aggiorna il record con auto-chiusura
        const updateData = {
          clockOutTime: autoCloseData.clockOutTime,
          clockOutDate: autoCloseData.clockOutDate,
          clockOutTimestamp: serverTimestamp(),
          totalHours: 8, // Sempre 8 ore per auto-chiusura
          standardHours: 8,
          overtimeHours: 0,
          isMultiDay: workResult.isMultiDay,
          workDistribution: [{
            date: record.date,
            totalHours: 8,
            standardHours: 8,
            overtimeHours: 0,
            notes: autoCloseData.autoClosedReason
          }],
          status: "auto-closed",
          autoClosedReason: autoCloseData.autoClosedReason,
          scanInfo: {
            ...(record.scanInfo || {}),
            autoClose: {
              originalDuration: `${durationHours.toFixed(2)}h`,
              autoClosedAt: now.toISOString(),
              reason: `Uscita non timbrata entro ${FORGOTTEN_CLOCKOUT_HOURS} ore`
            }
          },
          updatedAt: serverTimestamp()
        };
        
        const docRef = doc(db, "timekeeping", docSnap.id);
        await updateDoc(docRef, updateData);
        
        // Sincronizza con workHours usando sistema unificato
        try {
          await syncToWorkHoursUnified(userId, updateData.workDistribution, 'auto-chiusura');
          console.log(`✅ [AutoClose] Sincronizzazione workHours completata per ${record.date}`);
        } catch (syncError) {
          console.error(`❌ [AutoClose] Errore sincronizzazione:`, syncError);
        }
        
        closedCount++;
        console.log(`✅ [AutoClose] Sessione auto-chiusa: ${record.date} ${record.clockInTime} → ${autoCloseData.clockOutTime}`);
      }
    }
    
    console.log(`📊 [AutoClose] Completato: ${processedCount} processate, ${closedCount} auto-chiuse`);
    
    return { processed: processedCount, closed: closedCount };
    
  } catch (error) {
    console.error("❌ [AutoClose] Errore durante auto-chiusura:", error);
    return { processed: 0, closed: 0, error: error.message };
  }
};

/**
 * =====================================================
 * 🎯 SERVIZIO TIMEKEEPING UNIFICATO
 * =====================================================
 */
const timekeepingService = {
  
  /**
 * 🔵 REGISTRA INGRESSO - VERSIONE CON GESTIONE POSIZIONE
 * 
 * @param {string} userId - ID dell'utente che timbra
 * @param {string|null} locationId - ID della posizione (cantiere/officina) - opzionale
 * @param {Object} scanInfo - Informazioni scanner (deviceId, isAdmin, deviceType, etc.)
 * @returns {Object} Dati della timbratura creata
 */
  async clockIn(userId, locationId = null, scanInfo = {}) {
    try {
      const normalizedScanner = normalizeScanner(scanInfo);
      
      console.log(`🔵 [ClockIn] Tentativo ingresso per user: ${userId}`);
      console.log(`📱 [ClockIn] Scanner: ${normalizedScanner.scannerType} (${normalizedScanner.deviceType})`);

      // 🔒 Blocca la timbratura se il dispositivo è stato messo in pausa dall'admin
      await assertDeviceActive(normalizedScanner.deviceId);

      // ✅ GESTIONE POSIZIONE
      let clockInLocation = locationId;
      
      // Se è un kiosk e non è stata fornita una posizione, recuperala automaticamente
      if (normalizedScanner.scannerType === 'kiosk' && !clockInLocation && scanInfo.deviceId) {
        console.log(`🏗️ [ClockIn] Kiosk mode: recupero posizione da deviceId ${scanInfo.deviceId}`);
        
        try {
          clockInLocation = await getKioskDeviceLocation(scanInfo.deviceId);
          console.log(`✅ [ClockIn] Posizione kiosk recuperata: ${clockInLocation}`);
        } catch (locationError) {
          console.warn(`⚠️ [ClockIn] Errore recupero posizione kiosk: ${locationError.message}`);
          // Continua senza posizione se il recupero fallisce
        }
      }
      
      // Log della posizione finale
      if (clockInLocation) {
        try {
          const locationName = await getLocationName(clockInLocation);
          console.log(`📍 [ClockIn] Timbratura con posizione: ${locationName} (${clockInLocation})`);
        } catch (err) {
          console.log(`📍 [ClockIn] Timbratura con posizione: ${clockInLocation}`);
        }
      } else {
        console.log(`📍 [ClockIn] Timbratura senza posizione specificata`);
      }
      
      // Verifica utente
      const userRef = doc(db, "users", userId);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        throw new Error("Utente non trovato nel sistema");
      }
      
      const userData = userSnap.data();
      if (userData.qrStatus && userData.qrStatus.active === false) {
        throw new Error("QR code disattivato dall'amministratore");
      }
      
      // Calcola data e ora
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const timeString = `${hours}:${minutes}`;
      
      console.log(`🔍 [ClockIn] Controllo sessioni esistenti per ${userId} del ${dateString}`);
      
      const timekeepingRef = collection(db, "timekeeping");
      
      // 🎯 CONTROLLO UNIFICATO: Una sola sessione attiva per utente
      const activeQuery = query(
        timekeepingRef,
        where("userId", "==", userId),
        where("status", "==", "in-progress")
      );
      
      const activeSnapshot = await getDocs(activeQuery);
      
      if (!activeSnapshot.empty) {
        const activeRecord = activeSnapshot.docs[0].data();
        const activeScannerType = activeRecord.scanInfo?.clockIn?.scannerType || 'sconosciuto';
        
        console.log(`⚠️ [ClockIn] Sessione già attiva trovata:`);
        console.log(`   - Data: ${activeRecord.date} alle ${activeRecord.clockInTime}`);
        console.log(`   - Scanner apertura: ${activeScannerType}`);
        console.log(`   - Scanner corrente: ${normalizedScanner.scannerType}`);
        
        throw new Error(`Hai già un ingresso attivo del ${activeRecord.date} alle ${activeRecord.clockInTime}. Prima devi timbrare l'USCITA.`);
      }
      
      console.log(`✅ [ClockIn] Nessuna sessione attiva - Creazione nuovo record`);
      
      // Conta i turni già completati oggi (per numerazione)
      const todayQuery = query(
        timekeepingRef,
        where("userId", "==", userId),
        where("date", "==", dateString),
        where("status", "in", ["completed", "auto-closed"])
      );
      
      const todaySnapshot = await getDocs(todayQuery);
      const shiftNumber = todaySnapshot.size + 1;
      
      if (todaySnapshot.size > 0) {
        console.log(`🔄 [ClockIn] Turni già completati oggi: ${todaySnapshot.size} - Nuovo turno #${shiftNumber}`);
      }
      
      // ✅ CREA RECORD CON POSIZIONE
      const recordData = {
        userId,
        userName: userData.nome && userData.cognome ? 
          `${userData.nome} ${userData.cognome}` : userData.email,
        userEmail: userData.email,
        userCategory: isAutista(userData) ? 'Autista' : 'Standard',
        date: dateString,
        clockInTime: timeString,
        clockInTimestamp: serverTimestamp(),
        clockInLocation: clockInLocation || null, // ✅ CAMPO LOCATION
        clockOutTime: null,
        clockOutDate: null,
        clockOutTimestamp: null,
        totalHours: null,
        standardHours: null,
        overtimeHours: null,
        status: "in-progress",
        year: year.toString(),
        month: month.toString(),
        day: day.toString(),
        isMultiDay: false,
        workDistribution: null,
        shiftNumber: shiftNumber,
        // 🎯 TRACCIABILITÀ UNIFICATA
        scanInfo: {
          clockIn: normalizedScanner
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(timekeepingRef, recordData);
      console.log(`✅ [ClockIn] Record creato con ID: ${docRef.id}`);
      
      // 🆕 ============================================================================
      // GESTIONE RAPPORTINO AUTOMATICO
      // ============================================================================
      if (clockInLocation) {
        console.log(`📋 [ClockIn] Gestione rapportino automatico per location: ${clockInLocation}`);
        
        try {
          const rapportinoResult = await rapportiniAutoService.handleTimbratureAutoRapportino(
            userId,
            {
              ...recordData,
              id: docRef.id,
              clockInLocation: clockInLocation // Passa il campo location
            }
          );
          
          if (rapportinoResult.success) {
            console.log(`✅ [ClockIn] ${rapportinoResult.message}`);
            if (rapportinoResult.rapportinoCreated) {
              console.log(`📝 [ClockIn] Nuovo rapportino creato: ${rapportinoResult.rapportinoId}`);
            } else {
              console.log(`🔄 [ClockIn] Rapportino esistente aggiornato: ${rapportinoResult.rapportinoId}`);
            }
          } else {
            console.error(`⚠️ [ClockIn] Errore gestione rapportino:`, rapportinoResult.message);
          }
        } catch (rapportinoError) {
          // ⚠️ IMPORTANTE: Non bloccare la timbratura se il rapportino fallisce
          console.error(`❌ [ClockIn] Errore rapportino (non bloccante):`, rapportinoError);
          // La timbratura è già salvata, quindi continuiamo
        }
      } else {
        console.log(`ℹ️ [ClockIn] Nessuna location - rapportino non gestito`);
      }
      // ============================================================================
      
      // ✅ MESSAGGIO CON POSIZIONE
      let message = `✅ INGRESSO REGISTRATO\n`;
      message += `👤 ${recordData.userName}\n`;
      message += `📱 Scanner: ${normalizedScanner.scannerType.toUpperCase()}\n`;
      message += `📅 Data: ${dateString}\n`;
      message += `🕐 Orario: ${timeString}`;
      
      // Aggiungi nome posizione al messaggio se disponibile
      if (clockInLocation) {
        try {
          const locationName = await getLocationName(clockInLocation);
          message += `\n📍 Posizione: ${locationName}`;
        } catch (err) {
          console.warn(`⚠️ [ClockIn] Impossibile recuperare nome posizione: ${err.message}`);
        }
      }
      
      if (shiftNumber > 1) {
        message += `\n🔄 Turno #${shiftNumber} per oggi`;
      }
      
      console.log(`📝 [ClockIn] ${message.replace(/\n/g, ' | ')}`);
      
      return {
        id: docRef.id,
        ...recordData,
        message,
        clockInTime: timeString,
        date: dateString,
        userName: recordData.userName,
        scannerInfo: normalizedScanner,
        locationId: clockInLocation // ✅ Ritorna anche locationId
      };
      
    } catch (error) {
      console.error("❌ [ClockIn] Errore durante ingresso:", error);
      throw error;
    }
  },

  /**
   * 🔴 REGISTRA USCITA - VERSIONE UNIFICATA
   * Funziona identicamente per tutti gli scanner (kiosk, admin, responsabile)
   */
  async clockOut(userId, scanInfo = {}) {
    try {
      const normalizedScanner = normalizeScanner(scanInfo);
      
      console.log(`🔴 [ClockOut] Tentativo uscita per user: ${userId}`);
      console.log(`📱 [ClockOut] Scanner: ${normalizedScanner.scannerType} (${normalizedScanner.deviceType})`);

      // 🔒 Blocca la timbratura se il dispositivo è stato messo in pausa dall'admin
      await assertDeviceActive(normalizedScanner.deviceId);

      const userRef = doc(db, "users", userId);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        throw new Error("Utente non trovato nel sistema");
      }
      
      const userData = userSnap.data();
      
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const currentDateString = `${year}-${month}-${day}`;
      
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const currentTimeString = `${hours}:${minutes}`;
      
      console.log(`🔍 [ClockOut] Ricerca sessione attiva per ${userId}`);
      
      const timekeepingRef = collection(db, "timekeeping");
      
      // 🎯 RICERCA UNIFICATA: Trova l'unica sessione attiva (indipendentemente dallo scanner di apertura)
      const activeQuery = query(
        timekeepingRef,
        where("userId", "==", userId),
        where("status", "==", "in-progress")
      );
      
      const activeSnapshot = await getDocs(activeQuery);
      
      if (activeSnapshot.empty) {
        throw new Error(`Nessun ingresso attivo trovato. Prima devi timbrare l'INGRESSO.`);
      }
      
      console.log(`📊 [ClockOut] Trovate ${activeSnapshot.size} sessioni attive`);
      
      // Ordina manualmente per timestamp (più recente prima)
      const activeRecords = activeSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      activeRecords.sort((a, b) => {
        const aTime = a.clockInTimestamp?.toDate?.() || new Date(a.clockInTimestamp || 0);
        const bTime = b.clockInTimestamp?.toDate?.() || new Date(b.clockInTimestamp || 0);
        return bTime - aTime;
      });
      
      const record = activeRecords[0]; // Sessione più recente
      const openScannerType = record.scanInfo?.clockIn?.scannerType || 'sconosciuto';
      
      console.log(`🔍 [ClockOut] Sessione da chiudere:`);
      console.log(`   - ID: ${record.id}`);
      console.log(`   - Apertura: ${record.date} ${record.clockInTime} (scanner: ${openScannerType})`);
      console.log(`   - Chiusura: ${currentDateString} ${currentTimeString} (scanner: ${normalizedScanner.scannerType})`);
      
      const docRef = doc(db, "timekeeping", record.id);
      
      // 🎯 VALIDAZIONE UNIFICATA
      const validation = validateClockOutTime(
        record.clockInTime, 
        record.date, 
        currentTimeString, 
        currentDateString
      );
      
      let updateData;
      let workResult;
      let successMessage;
      
      if (!validation.isValid && validation.autoClose) {
        // 🚨 CASO AUTO-CHIUSURA: uscita dimenticata (turno > 18 ore)
        console.log(`🚨 [ClockOut] AUTO-CHIUSURA: ${validation.reason}`);
        
        const autoCloseData = validation.autoCloseData;
        
        // Calcolo unificato per auto-chiusura (sempre 8 ore standard)
        workResult = {
          totalHours: 8,
          standardHours: 8,
          overtimeHours: 0,
          isMultiDay: autoCloseData.clockOutDate !== record.date,
          workDistribution: [{
            date: record.date,
            totalHours: 8,
            standardHours: 8,
            overtimeHours: 0,
            notes: autoCloseData.autoClosedReason
          }],
          userCategory: isAutista(userData) ? 'Autista' : 'Standard',
          message: `Auto-chiuso: 8 ore standard assegnate`
        };
        
        updateData = {
          clockOutTime: autoCloseData.clockOutTime,
          clockOutDate: autoCloseData.clockOutDate,
          clockOutTimestamp: serverTimestamp(),
          totalHours: workResult.totalHours,
          standardHours: workResult.standardHours,
          overtimeHours: workResult.overtimeHours,
          isMultiDay: workResult.isMultiDay,
          workDistribution: workResult.workDistribution,
          userCategory: workResult.userCategory,
          status: "auto-closed",
          autoClosedReason: autoCloseData.autoClosedReason,
          scanInfo: {
            ...(record.scanInfo || {}),
            clockOut: {
              ...normalizedScanner,
              autoClose: true,
              originalTime: currentTimeString,
              originalDate: currentDateString
            }
          },
          updatedAt: serverTimestamp()
        };
        
        successMessage = `⚠️ TURNO AUTO-CHIUSO\n` +
                        `👤 ${record.userName}\n` +
                        `📱 Chiuso da: ${normalizedScanner.scannerType.toUpperCase()}\n` +
                        `⏰ Durata eccessiva rilevata (>${FORGOTTEN_CLOCKOUT_HOURS}h)\n` +
                        `📊 Ore assegnate: 8h standard + 0h straordinario\n` +
                        `🕐 Orario: ${record.clockInTime} → ${autoCloseData.clockOutTime}`;
        
      } else if (!validation.isValid) {
        // ❌ ERRORE DI VALIDAZIONE
        throw new Error(validation.reason);
        
      } else {
        // ✅ CASO NORMALE: Turno valido - CALCOLO UNIFICATO
        console.log(`✅ [ClockOut] Turno valido - Calcolo ore unificato`);
        
        workResult = calculateWorkingHoursUnified(
          record.clockInTime,
          record.date,
          currentTimeString,
          currentDateString,
          userData
        );
        
        updateData = {
          clockOutTime: currentTimeString,
          clockOutDate: currentDateString,
          clockOutTimestamp: serverTimestamp(),
          totalHours: workResult.totalHours,
          standardHours: workResult.standardHours,
          overtimeHours: workResult.overtimeHours,
          isMultiDay: workResult.isMultiDay,
          workDistribution: workResult.workDistribution,
          userCategory: workResult.userCategory,
          categoryRules: workResult.categoryRules,
          status: "completed",
          scanInfo: {
            ...(record.scanInfo || {}),
            clockOut: normalizedScanner
          },
          updatedAt: serverTimestamp()
        };
        
        successMessage = `✅ USCITA REGISTRATA\n`;
        successMessage += `👤 ${record.userName}\n`;
        successMessage += `📱 Aperto: ${openScannerType.toUpperCase()} | Chiuso: ${normalizedScanner.scannerType.toUpperCase()}\n`;
        successMessage += `⏱️ Ore totali: ${workResult.totalHours}h\n`;
        successMessage += `📊 Standard: ${workResult.standardHours}h | Straordinario: ${workResult.overtimeHours}h`;
        
        
        if (workResult.isMultiDay) {
          successMessage += `\n🌅 Turno multi-data distribuito su ${workResult.workDistribution.length} giorni`;
          workResult.workDistribution.forEach(day => {
            successMessage += `\n  • ${day.date}: ${day.totalHours}h (${day.standardHours}h std + ${day.overtimeHours}h straord.)`;
          });
        }
      }
      
      // 💾 AGGIORNA RECORD
      await updateDoc(docRef, updateData);
      console.log(`✅ [ClockOut] Record aggiornato: ${docRef.id}`);
      
      // 🆕 ============================================================================
      // GESTIONE RAPPORTINO AUTOMATICO - AGGIORNAMENTO ORE
      // ============================================================================
      if (record.clockInLocation) {
        console.log(`📋 [ClockOut] Aggiornamento rapportino con ore definitive...`);
        console.log(`📍 [ClockOut] Location da record: ${record.clockInLocation}`);
        
        try {
          // Prepara i dati completi per l'aggiornamento (con le ore calcolate)
          const completedData = {
            ...record,
            ...updateData,
            id: record.id,
            status: updateData.status || 'completed',
            clockInLocation: record.clockInLocation // Campo location dal record di ingresso
          };
          
          const rapportinoResult = await rapportiniAutoService.handleTimbratureAutoRapportino(
            userId,
            completedData
          );
          
          if (rapportinoResult.success) {
            console.log(`✅ [ClockOut] ${rapportinoResult.message}`);
            console.log(`📊 [ClockOut] Rapportino ${rapportinoResult.rapportinoId} aggiornato con ore:`);
            console.log(`   - Ore totali: ${updateData.totalHours}h`);
            console.log(`   - Ore ordinarie: ${updateData.standardHours}h`);
            console.log(`   - Ore straordinarie: ${updateData.overtimeHours}h`);
          } else {
            console.error(`⚠️ [ClockOut] Errore aggiornamento rapportino:`, rapportinoResult.message);
          }
        } catch (rapportinoError) {
          // ⚠️ IMPORTANTE: Non bloccare l'uscita se l'aggiornamento rapportino fallisce
          console.error(`❌ [ClockOut] Errore rapportino (non bloccante):`, rapportinoError);
          console.error(`❌ [ClockOut] Stack trace:`, rapportinoError.stack);
          // La timbratura è già salvata correttamente, quindi continuiamo
        }
      } else {
        console.log(`ℹ️ [ClockOut] Nessuna location nel record - rapportino non gestito`);
      }
      // ============================================================================
      
      // 🔄 SINCRONIZZAZIONE UNIFICATA
      try {
        console.log(`🔄 [ClockOut] Avvio sincronizzazione workHours unificata...`);
        const syncResults = await syncToWorkHoursUnified(userId, workResult.workDistribution);
        console.log(`✅ [ClockOut] Sincronizzazione completata:`, syncResults);
      } catch (syncError) {
        console.error(`❌ [ClockOut] Errore sincronizzazione workHours:`, syncError);
      }
      
      const userName = userData.nome && userData.cognome ? 
        `${userData.nome} ${userData.cognome}` : userData.email;
      
      console.log(`📝 [ClockOut] ${successMessage.replace(/\n/g, ' | ')}`);
      
      return {
        id: docRef.id,
        ...record,
        ...updateData,
        userName,
        message: successMessage,
        workCalculation: workResult,
        isAutoClose: validation.autoClose || false,
        scannerInfo: {
          openedBy: openScannerType,
          closedBy: normalizedScanner.scannerType
        }
      };
      
    } catch (error) {
      console.error("❌ [ClockOut] Errore durante uscita:", error);
      throw error;
    }
  },

  /**
   * 📊 OTTIENI STATO TIMBRATURE DI OGGI - VERSIONE UNIFICATA
   */
  async getTodayStatus(userId) {
    try {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      
      const timekeepingRef = collection(db, "timekeeping");
      
      // Auto-chiudi sessioni scadute
      try {
        await autoCloseOpenSessions(userId);
      } catch (closeError) {
        console.error("❌ Errore auto-chiusura sessioni:", closeError);
      }
      
      // Cerca sessioni in corso
      const activeQuery = query(
        timekeepingRef,
        where("userId", "==", userId),
        where("status", "==", "in-progress")
      );
      
      const activeSnapshot = await getDocs(activeQuery);
      
      if (!activeSnapshot.empty) {
        // Ordina per timestamp
        const activeRecords = activeSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        activeRecords.sort((a, b) => {
          const aTime = a.clockInTimestamp?.toDate?.() || new Date(a.clockInTimestamp || 0);
          const bTime = b.clockInTimestamp?.toDate?.() || new Date(b.clockInTimestamp || 0);
          return bTime - aTime;
        });
        
        const activeRecord = activeRecords[0];
        const openScannerType = activeRecord.scanInfo?.clockIn?.scannerType || 'sconosciuto';
        
        return {
          status: "in-progress",
          message: `Turno in corso: iniziato alle ${activeRecord.clockInTime}`,
          date: activeRecord.date,
          clockInTime: activeRecord.clockInTime,
          userCategory: activeRecord.userCategory || 'Standard',
          scannerInfo: {
            openedBy: openScannerType
          },
          timestamp: new Date().toISOString()
        };
      }
      
      // Cerca turni completati oggi
      const todayQuery = query(
        timekeepingRef,
        where("userId", "==", userId),
        where("date", "==", dateString),
        where("status", "in", ["completed", "auto-closed"])
      );
      
      const todaySnapshot = await getDocs(todayQuery);
      
      if (!todaySnapshot.empty) {
        // Ordina per timestamp
        const todayRecords = todaySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        todayRecords.sort((a, b) => {
          const aTime = a.clockInTimestamp?.toDate?.() || new Date(a.clockInTimestamp || 0);
          const bTime = b.clockInTimestamp?.toDate?.() || new Date(b.clockInTimestamp || 0);
          return bTime - aTime;
        });
        
        const todayRecord = todayRecords[0];
        const openScannerType = todayRecord.scanInfo?.clockIn?.scannerType || 'sconosciuto';
        const closeScannerType = todayRecord.scanInfo?.clockOut?.scannerType || 'sconosciuto';
        
        if (todayRecord.status === "completed") {
          return {
            status: "completed",
            message: `Turno completato: ${todayRecord.clockInTime} - ${todayRecord.clockOutTime}`,
            date: dateString,
            clockInTime: todayRecord.clockInTime,
            clockOutTime: todayRecord.clockOutTime,
            totalHours: todayRecord.totalHours,
            standardHours: todayRecord.standardHours,
            overtimeHours: todayRecord.overtimeHours,
            userCategory: todayRecord.userCategory || 'Standard',
            isMultiDay: todayRecord.isMultiDay,
            scannerInfo: {
              openedBy: openScannerType,
              closedBy: closeScannerType
            },
            timestamp: new Date().toISOString()
          };
        } else if (todayRecord.status === "auto-closed") {
          return {
            status: "auto-closed",
            message: `Turno auto-chiuso: ${todayRecord.clockInTime} - ${todayRecord.clockOutTime}`,
            date: dateString,
            clockInTime: todayRecord.clockInTime,
            clockOutTime: todayRecord.clockOutTime,
            totalHours: todayRecord.totalHours,
            standardHours: todayRecord.standardHours,
            overtimeHours: todayRecord.overtimeHours,
            userCategory: todayRecord.userCategory || 'Standard',
            autoClosedReason: todayRecord.autoClosedReason,
            scannerInfo: {
              openedBy: openScannerType,
              closedBy: closeScannerType
            },
            timestamp: new Date().toISOString()
          };
        }
      }
      
      // Nessun record trovato per oggi
      return {
        status: "no-active-shift",
        message: "Nessuna timbratura attiva per oggi",
        date: dateString,
        timestamp: new Date().toISOString()
      };
      
    } catch (error) {
      console.error("❌ Errore getTodayStatus:", error);
      throw error;
    }
  },

  /**
   * 🧹 AUTO-CHIUSURA SESSIONI SCADUTE - VERSIONE UNIFICATA
   */
  autoCloseOpenSessions,

  /**
   * 📋 Elenco dei dispositivi kiosk censiti dall'amministratore
   * (tab "Dispositivi" del pannello admin). È l'unica fonte valida di ID e nomi:
   * il kiosk sceglie da questa lista, non inventa identificativi.
   */
  async getRegisteredKioskDevices() {
    const snapshot = await getDocs(collection(db, "scanDevices"));
    return snapshot.docs
      .map(docSnap => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          deviceName: data.deviceName || docSnap.id,
          active: data.active !== false,
          assignedLocation: data.assignedLocation || null
        };
      })
      .sort((a, b) => a.deviceName.localeCompare(b.deviceName, 'it'));
  },

  /**
   * 🔎 Legge un dispositivo censito. Restituisce null se l'admin l'ha eliminato.
   */
  async getKioskDevice(deviceId) {
    const deviceSnap = await getDoc(doc(db, "scanDevices", deviceId));
    if (!deviceSnap.exists()) return null;
    const data = deviceSnap.data();
    return {
      id: deviceSnap.id,
      deviceName: data.deviceName || deviceSnap.id,
      active: data.active !== false,
      assignedLocation: data.assignedLocation || null
    };
  },

  /**
   * 📱 Collega questo tablet a un dispositivo GIÀ censito dall'amministratore.
   *
   * Prima il kiosk creava un documento nuovo per qualunque ID digitato: ogni
   * piccola variazione di nome generava un dispositivo "fantasma" senza
   * posizione assegnata, e le timbrature perdevano il cantiere. Ora il
   * documento deve esistere; aggiorniamo solo le info tecniche del tablet.
   * Nome, posizione e stato attivo/pausa restano quelli decisi dall'admin
   * (in particolare NON riattiviamo un dispositivo messo in pausa).
   */
  async bindKioskDevice(deviceId, deviceInfo = {}) {
    const deviceRef = doc(db, "scanDevices", deviceId);
    const deviceSnap = await getDoc(deviceRef);

    if (!deviceSnap.exists()) {
      throw new Error("Dispositivo non censito. Crealo prima dal pannello amministratore (Dispositivi).");
    }

    await updateDoc(deviceRef, {
      deviceInfo,
      lastActivity: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    const data = deviceSnap.data();
    console.log(`✅ Tablet collegato al dispositivo ${data.deviceName || deviceId} (${deviceId})`);
    return {
      id: deviceId,
      deviceName: data.deviceName || deviceId,
      active: data.active !== false,
      assignedLocation: data.assignedLocation || null
    };
  },

  /**
   * 💓 Segnala che il kiosk è vivo e con quale build gira, così dal pannello
   * admin si vede quali tablet hanno preso l'ultimo deploy. Non bloccante.
   */
  async reportKioskHeartbeat(deviceId, appBuild) {
    try {
      await updateDoc(doc(db, "scanDevices", deviceId), {
        appBuild,
        lastActivity: serverTimestamp()
      });
    } catch (error) {
      console.warn("⚠️ Heartbeat kiosk non riuscito (non bloccante):", error?.message || error);
    }
  },

  /**
   * 🔄 Sincronizza record offline (per modalità offline)
   */
  async syncOfflineRecords(offlineRecords) {
    try {
      console.log(`🔄 Sincronizzazione ${offlineRecords.length} record offline`);
      
      let successCount = 0;
      let failedCount = 0;
      const errors = [];
      
      for (const record of offlineRecords) {
        try {
          if (record.type === 'clockIn') {
            await this.clockIn(record.userId, record.scanInfo);
          } else if (record.type === 'clockOut') {
            await this.clockOut(record.userId, record.scanInfo);
          }
          successCount++;
        } catch (error) {
          console.error("❌ Errore sincronizzazione record:", error, record);
          failedCount++;
          errors.push({
            record,
            error: error.message
          });
        }
      }
      
      console.log(`✅ Sincronizzazione completata: ${successCount} successi, ${failedCount} falliti`);
      
      return {
        success: successCount,
        failed: failedCount,
        total: offlineRecords.length,
        errors
      };
      
    } catch (error) {
      console.error("❌ Errore sincronizzazione record offline:", error);
      throw error;
    }
  },

  /**
   * 📊 Ottieni statistiche timbrature per un utente
   */
  async getUserTimekeepingStats(userId, startDate, endDate) {
    try {
      console.log(`📊 Caricamento statistiche per user ${userId} da ${startDate} a ${endDate}`);
      
      const timekeepingRef = collection(db, "timekeeping");
      const q = query(
        timekeepingRef,
        where("userId", "==", userId),
        where("date", ">=", startDate),
        where("date", "<=", endDate),
        where("status", "in", ["completed", "auto-closed"])
      );
      
      const querySnapshot = await getDocs(q);
      const records = [];
      
      querySnapshot.forEach(doc => {
        records.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      // Calcola statistiche
      const totalHours = records.reduce((sum, record) => sum + (record.totalHours || 0), 0);
      const standardHours = records.reduce((sum, record) => sum + (record.standardHours || 0), 0);
      const overtimeHours = records.reduce((sum, record) => sum + (record.overtimeHours || 0), 0);
      const autoClosedCount = records.filter(record => record.status === "auto-closed").length;
      const completedCount = records.filter(record => record.status === "completed").length;
      
      console.log(`📊 Statistiche: ${records.length} record, ${totalHours}h totali`);
      
      return {
        totalRecords: records.length,
        totalHours,
        standardHours,
        overtimeHours,
        completedCount,
        autoClosedCount,
        records
      };
      
    } catch (error) {
      console.error("❌ Errore statistiche timbrature:", error);
      throw error;
    }
  },

  /**
   * 📋 Ottieni record timbrature per data range
   */
  async getTimekeepingRecords(userId, startDate, endDate) {
    try {
      console.log(`📋 Caricamento record per user ${userId} da ${startDate} a ${endDate}`);
      
      const timekeepingRef = collection(db, "timekeeping");
      
      // Query semplificata senza orderBy multipli per evitare errori di indice
      const q = query(
        timekeepingRef,
        where("userId", "==", userId),
        where("date", ">=", startDate),
        where("date", "<=", endDate)
      );
      
      const querySnapshot = await getDocs(q);
      const records = [];
      
      querySnapshot.forEach(doc => {
        records.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      // Ordina manualmente i risultati
      records.sort((a, b) => {
        // Prima per data (desc)
        if (a.date !== b.date) {
          return b.date.localeCompare(a.date);
        }
        // Poi per timestamp (desc)
        const aTime = a.clockInTimestamp?.toDate?.() || new Date(0);
        const bTime = b.clockInTimestamp?.toDate?.() || new Date(0);
        return bTime - aTime;
      });
      
      console.log(`📋 Trovati ${records.length} record`);
      
      return records;
      
    } catch (error) {
      console.error("❌ Errore caricamento record timbrature:", error);
      throw error;
    }
  }
};

export default timekeepingService;